// ACC.A1 #809 (PR #812 Option C) — orientation chrome rendered above the
// chat history when a session has a populated session_checkpoint.
//
// Lives separate from ChatHistory + ChatMessage on purpose: the banner
// is NOT a chat message — it's a UI hint to the user that they're
// resuming from a known dimension, distinct from anything the agent
// itself ever said. Folding it into agent_messages would (a) mutate the
// chat record on a GET, and (b) muddle the conversation history with
// derived state. Keeping it as its own component above ChatHistory
// keeps the chat record pure and makes the banner trivial to restyle
// or relocate later.
//
// Renders nothing when `message` is null (the common case — most
// sessions are fresh or use agents without checkpoint label support).
// The parent (ChatPage) reads `session.resumeBanner` from the API
// response and passes it through directly.

interface ResumeBannerProps {
  message: string | null;
}

export default function ResumeBanner({ message }: ResumeBannerProps) {
  if (!message) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="resume-banner"
      style={{
        margin: '12px 16px 8px',
        padding: '10px 14px',
        background: 'rgba(124, 58, 237, 0.06)',
        border: '1px solid rgba(124, 58, 237, 0.20)',
        borderRadius: 'var(--radius-sm, 6px)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      {/* Orientation icon — distinct visual signal that this is system
          chrome, not a chat bubble. SVG inline so the component has no
          icon-library dependency. */}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--primary, #7c3aed)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0, marginTop: 2 }}
        aria-hidden="true"
      >
        {/* Clock-arrow ("resumed") glyph */}
        <path d="M3 12a9 9 0 1 0 9-9" />
        <path d="M3 4v5h5" />
        <path d="M12 7v5l3 2" />
      </svg>
      <p
        style={{
          fontFamily: 'var(--font-body, sans-serif)',
          fontSize: 13,
          lineHeight: 1.5,
          color: 'var(--text, #1a1a1a)',
          margin: 0,
        }}
      >
        {message}
      </p>
    </div>
  );
}
